<template>
  <div id="Customer">
    <PDFviewer v-if="isPDF" :src="fileUrl" />
    <template
      v-if="isPDF"
    >
      <ul
        ref="contextMenu"
        :class="['contextMenu', { 'contextMenu--active': isShowMenu }]"
      >
        <li class="contextMenu__item">
          <a
            class="contextMenu__link"
            :href="fileUrl"
            :download="`${originalFileName}.pdf`"
            target="_self"
          >
            <img
              class="contextMenu__icon"
              width="20" height="20"
              src="@/assets/images/icons/download.svg"
              alt="download"
            >
            <span class="contextMenu__label">下載報告</span>
          </a>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import PDFviewer from '@/components/viewer/PDFviewer';
import updateCSSVariable from '@/utils/stylesheetManager';

export default {
  name: 'CustomerView',
  components: {
    PDFviewer,
  },
  data() {
    return {
      isShowMenu: false,
      fileUrl: '',
      numPages: undefined,
      isPDF: false,
      touchTimer: null,
      originalFileName: '',
    };
  },
  methods: {
    handleTouchEvent() {
      const that = this;
      function cancelTimeout() {
        clearTimeout(that.touchTimer);
      }

      function handleTouchStart(e) {
        that.touchTimer = setTimeout(() => {
          that.handleContextMenuDisplay(e);
        }, 300); // 300ms for long press detection
      }
      return {
        cancelTimeout,
        handleTouchStart,
      };
    },
    registerMobileEvent() {
      // Mobile long press (touchstart and touchend events)
      document.addEventListener('touchstart', this.handleTouchEvent().handleTouchStart, { passive: false });

      document.addEventListener('touchend', this.handleTouchEvent().cancelTimeout, { passive: false });

      document.addEventListener('touchcancel', this.handleTouchEvent().cancelTimeout, { passive: false });
    },
    handleClick() {
      if (this.isShowMenu) {
        this.isShowMenu = false;
      }
    },
    handleContextMenuDisplay(e) {
      e.preventDefault();
      this.isShowMenu = true;
      const { contextMenu: menu } = this.$refs;
      if (!menu) {
        this.isShowMenu = false;
        return;
      }

      const pageX = (e.pageX || e.touches[0].pageX);
      const pageY = (e.pageY || e.touches[0].pageY);

      updateCSSVariable('--context-menu-x', pageX + menu.offsetWidth > window.innerWidth
        ? `${pageX - menu.offsetWidth}px`
        : `${pageX}px`);
      updateCSSVariable('--context-menu-y', pageY + menu.offsetHeight > window.innerHeight
        ? `${pageY - menu.offsetHeight}px`
        : `${pageY}px`);
    },
    isMobileDevice() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },
    async getPathByApi() {
      const { reportApi } = this.$api;
      // 同時兼容原始檔案金鑰連結及短網址轉導
      const { sc: shortcode, token: fileToken, source } = this.$route.query;
      const apiData = {};

      if (fileToken) {
        apiData.filetoken = fileToken;
      }
      if (shortcode) {
        apiData.shortcode = shortcode;
      }
      if (source) {
        apiData.source = source;
      }
      const res = await reportApi.getReportPath(apiData);

      if (res.errorStatus) {
        // eslint-disable-next-line no-alert
        alert(res.message);
        this.$router.push('/');
      }

      return res.data;
    },
    async directToFile() {
      const path = await this.getPathByApi();
      const { filePath, uploadFileName } = path;
      this.originalFileName = uploadFileName;
      if (filePath) {
        this.fileUrl = filePath.replace('C:\\inetpub\\wwwroot\\', `${process.env.VUE_APP_TEMP_URL}`);
        this.fileUrl = this.fileUrl.replace(/\\/g, '/');
        this.isPDF = this.fileUrl.endsWith('.pdf');
        if (process.env.NODE_ENV === 'development') {
          this.fileUrl = this.fileUrl.replace(process.env.VUE_APP_TEMP_URL, '/api/file/');
        }
        if (this.isPDF) return;
        this.triggerAutoDownload(uploadFileName);
      }
    },
    triggerAutoDownload(fileName = 'report') {
      if (!this.fileUrl) return;

      fetch(this.fileUrl)
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;

          // Extract file extension from original fileUrl
          const urlExtension = this.fileUrl.split('.').pop();
          const fileNameWithExt = `${fileName}.${urlExtension}`;
          link.download = fileNameWithExt;
          link.target = '_self';
          document.body.appendChild(link);

          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          // Navigate back to home page after a short delay
          setTimeout(() => {
            this.$router.push('/');
          }, 100);
        })
        .catch((err) => {
          console.error('Download failed:', err);
          window.location.href = this.fileUrl;
        });
    },
  },
  async mounted() {
    await this.directToFile();
    if (this.isPDF) {
      const eventRegister = this.isMobileDevice()
        ? this.registerMobileEvent
        : () => { document.addEventListener('contextmenu', this.handleContextMenuDisplay); };
      eventRegister();
      document.addEventListener('click', this.handleClick);
      this.observer = new IntersectionObserver(([{ isIntersecting }]) => {
        if (!isIntersecting) {
          this.isShowMenu = false;
        }
      });
      this.observer.observe(this.$refs.contextMenu);
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
    document.removeEventListener('touchstart', this.handleTouchEvent().handleTouchStart, { passive: false });
    document.removeEventListener('touchend', this.handleTouchEvent().cancelTimeout, { passive: false });
    document.removeEventListener('touchcancel', this.handleTouchEvent().cancelTimeout, { passive: false });
    document.removeEventListener('contextmenu', this.handleContextMenuDisplay);
    document.removeEventListener('click', this.handleClick);
  },
};
</script>
<style lang="scss" scoped>
.contextMenu {
  $root: &;
  position: absolute;
  background-color: #fff;
  box-shadow: 0px;
  overflow: hidden;
  border-radius: 4px;
  padding: 4px;
  width: 100px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  left: var(--context-menu-x);
  top: var(--context-menu-y);
  display: none;
  &--active {
    display: block;
  }
  &__link {
    display: flex;
    align-items: center;
    border-radius: 5px;
    row-gap: 4px;
    width: 100%;
    padding: 3px;
    &:hover {
      color: #fff;
      background-color: #006DBA;
      #{$root}__icon {
        filter: brightness(0) invert(1);
      }
    }
  }
  &__icon {
    display: block;
  }
}
</style>
